import React, { Component } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Spinner,
  Alert,
} from 'reactstrap';
import moment from 'moment';
import {
  getServiceByName,
  getExecutionHistory,
  getServiceEvents,
} from '../../../services/monitoring.service';
import AuthService from '../../Auth/AuthService';
import PermissionFeature from '../../Permissions/PermissionFeature';

/**
 * Service Detail Modal Component
 * Shows detailed information about a specific service in a modal dialog
 *
 * Props:
 * - show: Boolean to control modal visibility
 * - serviceName: Name of the service to display details for
 * - onHide: Function to call when modal is closed
 *
 * Tabs:
 * 1. Execution History: Last 20 execution records (always visible)
 * 2. Service Events: Last 20 Windows Event Log entries (requires MonitoringDashboardViewServiceEvents permission)
 *
 * Permissions:
 * - MonitoringDashboardViewDetails: Required to view the modal
 * - MonitoringDashboardViewServiceEvents: Required to view Service Events tab
 */
class ServiceDetailModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 'history',
      service: null,
      executionHistory: [],
      serviceEvents: [],
      loading: true,
      error: null,
    };
  }

  componentDidUpdate(prevProps) {
    // Check permission before fetching data
    const canViewDetails = AuthService.canView(PermissionFeature.MonitoringDashboardViewDetails);

    if (!canViewDetails) {
      return; // Don't fetch data if user doesn't have permission
    }

    // Fetch data when modal is opened or service changes
    if (
      (this.props.show && !prevProps.show) ||
      (this.props.show && this.props.serviceName !== prevProps.serviceName)
    ) {
      this.fetchServiceDetails();
    }
  }

  fetchServiceDetails = async () => {
    const { serviceName } = this.props;

    if (!serviceName) return;

    try {
      this.setState({ loading: true, error: null });

      // Check if user has permission to view service events
      const canViewServiceEvents = AuthService.canView(PermissionFeature.MonitoringDashboardViewServiceEvents);

      // Fetch data - only fetch service events if user has permission
      const promises = [
        getServiceByName(serviceName),
        getExecutionHistory(serviceName, 20),
      ];

      if (canViewServiceEvents) {
        promises.push(getServiceEvents(serviceName, 20));
      }

      const responses = await Promise.all(promises);

      const service = responses[0].Data || responses[0].data || responses[0];
      const history = responses[1].Data || responses[1].data || responses[1];
      const events = canViewServiceEvents
        ? responses[2].Data || responses[2].data || responses[2]
        : [];

      this.setState({
        service,
        executionHistory: history,
        serviceEvents: events,
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching service details:', error);
      this.setState({
        error: error.message || 'Failed to load service details',
        loading: false,
      });
    }
  };

  toggleTab = (tab) => {
    if (this.state.activeTab !== tab) {
      this.setState({ activeTab: tab });
    }
  };

  renderServiceInfo = () => {
    const { service } = this.state;
    if (!service) return null;

    return (
      <div className="service-info mb-3 p-3 bg-light rounded">
        <div className="row">
          <div className="col-md-6">
            <strong>Service Name:</strong> {service.displayName}
          </div>
          <div className="col-md-6">
            <strong>Status:</strong>{' '}
            <span
              className={`badge ${
                service.isRunning === 1 ? 'badge-success' : 'badge-secondary'
              }`}
            >
              {service.isRunning === 1 ? 'Running' : 'Stopped'}
            </span>
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-md-6">
            <strong>Scheduling Type:</strong> {service.schedulingType}
          </div>
          <div className="col-md-6">
            <strong>Frequency:</strong> {service.executionFrequency}
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-md-6">
            <strong>Total Executions:</strong> {service.totalExecutions}
          </div>
          <div className="col-md-6">
            <strong>Success Rate:</strong>{' '}
            <span
              className={`badge ${
                service.successRate >= 95
                  ? 'badge-success'
                  : service.successRate >= 80
                  ? 'badge-warning'
                  : 'badge-danger'
              }`}
            >
              {service.successRate.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
    );
  };

  renderExecutionHistory = () => {
    const { executionHistory } = this.state;

    if (executionHistory.length === 0) {
      return (
        <Alert color="info">
          <i className="fa fa-info-circle mr-2"></i>
          No execution history available
        </Alert>
      );
    }

    return (
      <div className="table-responsive">
        <Table striped hover size="sm">
          <thead>
            <tr>
              <th style={{ width: '25%' }}>Execution Time</th>
              <th style={{ width: '15%' }}>Status</th>
              <th style={{ width: '15%' }}>Duration</th>
              <th style={{ width: '45%' }}>Error Message</th>
            </tr>
          </thead>
          <tbody>
            {executionHistory.map((execution) => (
              <tr key={execution.executionId}>
                <td>{moment.utc(execution.executionTime).local().format('YYYY-MM-DD HH:mm:ss')}</td>
                <td>
                  <span
                    className={`badge ${
                      execution.status === 'Success' ? 'badge-success' : 'badge-danger'
                    }`}
                  >
                    {execution.status}
                  </span>
                </td>
                <td>
                  {execution.duration
                    ? `${(execution.duration / 1000).toFixed(2)}s`
                    : 'N/A'}
                </td>
                <td>
                  {execution.errorMessage ? (
                    <small className="text-danger" title={execution.errorMessage}>
                      {execution.errorMessage.substring(0, 100)}
                      {execution.errorMessage.length > 100 ? '...' : ''}
                    </small>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  renderServiceEvents = () => {
    const { serviceEvents } = this.state;

    if (serviceEvents.length === 0) {
      return (
        <Alert color="info">
          <i className="fa fa-info-circle mr-2"></i>
          No service events available
        </Alert>
      );
    }

    return (
      <div className="table-responsive">
        <Table striped hover size="sm">
          <thead>
            <tr>
              <th style={{ width: '20%' }}>Event Time</th>
              <th style={{ width: '15%' }}>Event Type</th>
              <th style={{ width: '10%' }}>Event ID</th>
              <th style={{ width: '55%' }}>Message</th>
            </tr>
          </thead>
          <tbody>
            {serviceEvents.map((event) => (
              <tr key={event.eventId}>
                <td>{moment.utc(event.eventTime).local().format('YYYY-MM-DD HH:mm:ss')}</td>
                <td>
                  <span
                    className={`badge ${
                      event.eventType === 'Start'
                        ? 'badge-success'
                        : event.eventType === 'Stop'
                        ? 'badge-warning'
                        : 'badge-danger'
                    }`}
                  >
                    {event.eventType}
                  </span>
                </td>
                <td>
                  {event.windowsEventId ? (
                    <code>{event.windowsEventId}</code>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>
                  <small title={event.message}>
                    {event.message.substring(0, 150)}
                    {event.message.length > 150 ? '...' : ''}
                  </small>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  render() {
    const { show, serviceName, onHide } = this.props;
    const { activeTab, loading, error } = this.state;

    // Check permissions
    const canViewDetails = AuthService.canView(PermissionFeature.MonitoringDashboardViewDetails);
    const canViewServiceEvents = AuthService.canView(PermissionFeature.MonitoringDashboardViewServiceEvents);

    return (
      <Modal isOpen={show} toggle={onHide} size="xl">
        <ModalHeader toggle={onHide}>
          Service Details
          {serviceName && (
            <span className="ml-2 text-muted" style={{ fontSize: '0.9rem' }}>
              - {serviceName}
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          {!canViewDetails ? (
            <Alert color="warning">
              <h5 className="alert-heading">
                <i className="fa fa-lock mr-2"></i>
                Access Denied
              </h5>
              <p className="mb-0">
                You do not have permission to view service details. Please contact your system administrator to request access.
              </p>
            </Alert>
          ) : loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-3 text-muted">Loading service details...</p>
            </div>
          ) : error ? (
            <Alert color="danger">
              <i className="fa fa-exclamation-triangle mr-2"></i>
              {error}
            </Alert>
          ) : (
            <>
              {this.renderServiceInfo()}

              {/* Tabs */}
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={activeTab === 'history' ? 'active' : ''}
                    onClick={() => this.toggleTab('history')}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="fa fa-history mr-2"></i>
                    Execution History
                  </NavLink>
                </NavItem>
                {canViewServiceEvents && (
                  <NavItem>
                    <NavLink
                      className={activeTab === 'events' ? 'active' : ''}
                      onClick={() => this.toggleTab('events')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="fa fa-calendar-alt mr-2"></i>
                      Service Events
                    </NavLink>
                  </NavItem>
                )}
              </Nav>

              {/* Tab Content */}
              <TabContent activeTab={activeTab}>
                <TabPane tabId="history" className="pt-3">
                  {this.renderExecutionHistory()}
                </TabPane>
                {canViewServiceEvents && (
                  <TabPane tabId="events" className="pt-3">
                    {this.renderServiceEvents()}
                  </TabPane>
                )}
              </TabContent>
            </>
          )}
        </ModalBody>
      </Modal>
    );
  }
}

export default ServiceDetailModal;
